import React, { useMemo } from 'react';
import { Button } from '@mui/material';
import { useIntl } from 'react-intl';

import Dialog from '../Components/DesignSystem/Dialog';

interface DialogProps {
  title?: string;
  content?: string | React.ReactNode;
  label?: string;
  confirmText?: string;
  cancelText?: string;
}

interface UseConfirmationDialogReturn {
  dialog: React.ReactNode;
  confirm: (config?: DialogProps) => Promise<boolean>;
}

type ConfirmationStatus = 'confirm' | 'cancel';

export default function useConfirmationDialog(id: string): UseConfirmationDialogReturn {
  const intl = useIntl();
  const [open, setOpen] = React.useState(false);
  const defaultDialogProps: DialogProps = {
    title: intl.formatMessage({ id: 'GENERIC_CONFIRM_DELETE.TITLE' }),
    content: intl.formatMessage({ id: 'GENERIC_CONFIRM_DELETE.CONTENT' }),
    label: '',
    confirmText: intl.formatMessage({ id: 'GENERIC_CONFIRM_DELETE.CTA' }),
    cancelText: intl.formatMessage({ id: 'BUTTONS.NO' }),
  };

  const [dialogProps, setDialogProps] = React.useState<DialogProps>(defaultDialogProps);

  const statusRef = React.useRef<ConfirmationStatus | null>(null);

  const waitForConfirmation = async () => {
    return new Promise<boolean>((resolve) => {
      // check every 100ms if the user has clicked to confirm or cancel
      const interval = setInterval(() => {
        if (statusRef.current) {
          // if the user has clicked on a button, we resolve the promise
          // clear the interval
          // and we reset all the states
          clearInterval(interval);
          resolve(statusRef.current === 'confirm');
          statusRef.current = null;
          setDialogProps(defaultDialogProps);
          setOpen(false);
        }
      }, 100);
    });
  };

  // confirm but with useCallback
  const confirm = React.useCallback(
    async (config: DialogProps = {}) => {
      setDialogProps({ ...dialogProps, ...config });
      setOpen(true);
      return waitForConfirmation();
    },
    [dialogProps],
  );

  const handleAction = (action: ConfirmationStatus) => () => {
    statusRef.current = action;
  };

  // You plug this somewhere in your component tree
  const dialog = useMemo(
    () => (
      <Dialog
        z-index={1000}
        id={`confirmation-dialog-${id}`}
        title={dialogProps.title}
        label={dialogProps.label}
        onClose={handleAction('cancel')}
        open={open}
        actions={
          <>
            <Button variant="outlined" onClick={handleAction('cancel')}>
              {dialogProps.cancelText}
            </Button>
            <Button variant="contained" onClick={handleAction('confirm')}>
              {dialogProps.confirmText}
            </Button>
          </>
        }
      >
        {dialogProps.content}
      </Dialog>
    ),
    [dialogProps, id, open],
  );

  return { dialog, confirm };
}
